import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";

// ---------------------------------------------------------------------------
// FR-D06: Shared Template Library page
//
// Features:
//   - Search by template name
//   - Filter by visibility (all, public, private, repository)
//   - Fork button with confirmation
//   - Fork count display
//   - Parent template link
// ---------------------------------------------------------------------------

type Visibility = "all" | "public" | "private" | "repository";

export function TemplateLibraryPage() {
  const templates = useQuery(api.promptTemplates.listTemplates);
  const forkTemplate = useMutation(api.promptTemplates.forkTemplate);

  const [search, setSearch] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState<Visibility>("all");
  const [forkingId, setForkingId] = useState<Id<"promptTemplates"> | null>(null);
  const [forkName, setForkName] = useState("");
  const [forkBy, setForkBy] = useState("");

  // Filter and search templates
  const filtered = useMemo(() => {
    if (!templates) return [];
    return templates.filter((t) => {
      // Search filter
      if (search && !t.name.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      // Visibility filter
      if (visibilityFilter !== "all") {
        const vis = t.visibility ?? "private";
        if (vis !== visibilityFilter) return false;
      }
      return true;
    });
  }, [templates, search, visibilityFilter]);

  const handleFork = useCallback(
    async (templateId: Id<"promptTemplates">) => {
      if (!forkName.trim() || !forkBy.trim()) return;
      try {
        await forkTemplate({
          templateId,
          newName: forkName.trim(),
          forkedBy: forkBy.trim(),
        });
        setForkingId(null);
        setForkName("");
        setForkBy("");
      } catch (err) {
        console.error("Fork failed:", err);
      }
    },
    [forkTemplate, forkName, forkBy]
  );

  // Find parent template name by ID
  const parentName = useCallback(
    (parentId: Id<"promptTemplates"> | undefined) => {
      if (!parentId || !templates) return null;
      const parent = templates.find((t) => t._id === parentId);
      return parent?.name ?? null;
    },
    [templates]
  );

  if (!templates) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        Loading templates...
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-zinc-100">
          Template Library
        </h1>
        <span className="text-sm text-zinc-500">
          {filtered.length} template{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Search and filter controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-indigo-500"
        />
        <select
          value={visibilityFilter}
          onChange={(e) => setVisibilityFilter(e.target.value as Visibility)}
          className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500"
        >
          <option value="all">All visibility</option>
          <option value="public">Public</option>
          <option value="private">Private</option>
          <option value="repository">Repository</option>
        </select>
      </div>

      {/* Template list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 text-sm">
          No templates match your search.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((template) => {
            const vis = template.visibility ?? "private";
            const forks = template.forkCount ?? 0;
            const parentId = template.parentTemplateId as
              | Id<"promptTemplates">
              | undefined;
            const parent = parentName(parentId);

            return (
              <div
                key={template._id}
                className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-3"
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-zinc-100 truncate">
                      {template.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span
                        className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${
                          vis === "public"
                            ? "bg-emerald-500/15 text-emerald-400"
                            : vis === "repository"
                              ? "bg-amber-500/15 text-amber-400"
                              : "bg-zinc-700/50 text-zinc-400"
                        }`}
                      >
                        {vis}
                      </span>
                      <span className="text-[11px] text-zinc-500">
                        v{template.version}
                      </span>
                      {template.createdBy && (
                        <span className="text-[11px] text-zinc-500">
                          by {template.createdBy}
                        </span>
                      )}
                      {template.active ? (
                        <span className="text-[11px] text-emerald-400">
                          active
                        </span>
                      ) : (
                        <span className="text-[11px] text-zinc-600">
                          inactive
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {/* Fork count */}
                    <span
                      className="text-xs text-zinc-500 flex items-center gap-1"
                      title={`${forks} fork${forks !== 1 ? "s" : ""}`}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
                        />
                      </svg>
                      {forks}
                    </span>

                    {/* Fork button */}
                    <button
                      onClick={() => {
                        setForkingId(template._id);
                        setForkName(`${template.name}-fork`);
                      }}
                      className="text-xs bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 rounded px-2.5 py-1 hover:bg-indigo-500/25 transition-colors"
                    >
                      Fork
                    </button>
                  </div>
                </div>

                {/* Parent template link */}
                {parent && (
                  <div className="text-[11px] text-zinc-500">
                    Forked from{" "}
                    <span className="text-indigo-400 font-medium">
                      {parent}
                    </span>
                  </div>
                )}

                {/* Content preview */}
                <div className="text-xs text-zinc-500 bg-zinc-950 rounded p-2 max-h-20 overflow-hidden">
                  <pre className="whitespace-pre-wrap font-mono">
                    {template.content.slice(0, 200)}
                    {template.content.length > 200 ? "..." : ""}
                  </pre>
                </div>

                {/* Fork form (inline, shown when this template is being forked) */}
                {forkingId === template._id && (
                  <div className="border-t border-zinc-800 pt-3 space-y-2">
                    <p className="text-xs text-zinc-400 font-medium">
                      Fork this template
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        placeholder="Fork name"
                        value={forkName}
                        onChange={(e) => setForkName(e.target.value)}
                        className="flex-1 bg-zinc-950 border border-zinc-700 rounded px-2.5 py-1.5 text-xs text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-indigo-500"
                      />
                      <input
                        type="text"
                        placeholder="Your email"
                        value={forkBy}
                        onChange={(e) => setForkBy(e.target.value)}
                        className="flex-1 bg-zinc-950 border border-zinc-700 rounded px-2.5 py-1.5 text-xs text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleFork(template._id)}
                        disabled={!forkName.trim() || !forkBy.trim()}
                        className="text-xs bg-indigo-600 text-white rounded px-3 py-1.5 hover:bg-indigo-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Confirm fork
                      </button>
                      <button
                        onClick={() => {
                          setForkingId(null);
                          setForkName("");
                          setForkBy("");
                        }}
                        className="text-xs text-zinc-400 hover:text-zinc-200 px-3 py-1.5 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
